import { ReactNode, useState } from "react";
import {
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSort,
  faSortDown,
  faSortUp,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { QueryResultColumnModel } from "services/api/cplapi/CPLAPI";
import {
  columnType,
  isNumeric,
} from "app/modules/aiAssistant/conversation/messages/components/resultsTable/resultsTableHelpers";

export type ResultsSort = { index: number; direction: "asc" | "desc" } | null;

export type ResultsGridTestIds = {
  exportCsvButton: string;
  sortColumnButton: (colIndex: number) => string;
  previousPageButton: string;
  nextPageButton: string;
};

type Props = {
  columns: QueryResultColumnModel[];
  formattedRows: string[][];
  sort: ResultsSort;
  onSort: (index: number) => void;
  onExportCsv: () => void;
  pageSize: number;
  isFullscreen?: boolean;
  isTruncated?: boolean;
  headerActions?: ReactNode;
  testIds: ResultsGridTestIds;
};

const ResultsGrid = ({
  columns,
  formattedRows,
  sort,
  onSort,
  onExportCsv,
  pageSize,
  isFullscreen = false,
  isTruncated,
  headerActions,
  testIds,
}: Props) => {
  const [page, setPage] = useState(0);

  const totalRows = formattedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const startIndex = safePage * pageSize;
  const pageRows = formattedRows.slice(startIndex, startIndex + pageSize);
  const rangeStart = totalRows === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + pageSize, totalRows);
  const showPagination = totalRows > pageSize;

  const sortIconFor = (index: number) => {
    if (sort == null || sort.index !== index) {
      return faSort;
    }

    return sort.direction === "asc" ? faSortUp : faSortDown;
  };

  return (
    <div className={`assistimo-results ${isFullscreen ? "assistimo-results--fullscreen" : ""}`}>
      <div className="assistimo-results__header">
        <span className="assistimo-results__count">
          {totalRows} {totalRows === 1 ? "row" : "rows"}
        </span>
        <div className="assistimo-results__actions">
          <button
            data-test-id={testIds.exportCsvButton}
            type="button"
            className="assistimo-results__action"
            onClick={onExportCsv}
          >
            <FontAwesomeIcon icon={faDownload} />
            Export CSV
          </button>
          {headerActions}
        </div>
      </div>
      <div className="assistimo-results__scroll">
        <table className="assistimo-results__table">
          <thead>
            <tr>
              {columns.map((col, colIndex) => {
                const numeric = isNumeric(columnType(col));
                const isSorted = sort?.index === colIndex;
                return (
                  <th
                    key={colIndex}
                    aria-sort={isSorted ? (sort?.direction === "asc" ? "ascending" : "descending") : undefined}
                  >
                    <button
                      data-test-id={testIds.sortColumnButton(colIndex)}
                      type="button"
                      className={`assistimo-results__sort-button ${
                        numeric ? "assistimo-results__sort-button--number" : ""
                      }`}
                      onClick={() => onSort(colIndex)}
                    >
                      <span>{col.name}</span>
                      <FontAwesomeIcon
                        icon={sortIconFor(colIndex)}
                        className={`assistimo-results__sort-icon ${
                          isSorted ? "assistimo-results__sort-icon--active" : ""
                        }`}
                      />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((formattedRow, rowIndex) => (
              <tr key={startIndex + rowIndex}>
                {formattedRow.map((cellText, colIndex) => (
                  <td
                    key={colIndex}
                    className={isNumeric(columnType(columns[colIndex])) ? "assistimo-results__cell--number" : ""}
                  >
                    <span className="assistimo-results__cell-text">{cellText}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <div className="assistimo-results__pagination">
          <span>
            Showing {rangeStart}–{rangeEnd} of {totalRows}
          </span>
          <div className="assistimo-results__page-controls">
            <button
              data-test-id={testIds.previousPageButton}
              type="button"
              className="assistimo-results__page-button"
              disabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
              aria-label="Previous page"
              title="Previous page"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="assistimo-results__page-indicator">
              Page {safePage + 1} of {totalPages}
            </span>
            <button
              data-test-id={testIds.nextPageButton}
              type="button"
              className="assistimo-results__page-button"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage(safePage + 1)}
              aria-label="Next page"
              title="Next page"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      )}
      {isTruncated && (
        <div className="assistimo-results__footer-note">
          Showing only {totalRows} results - More available. Narrow query if wanting specific results
        </div>
      )}
    </div>
  );
};

export default ResultsGrid;
